/* eslint-disable no-nested-ternary */
import React from "react";
import { Checkbox } from "@mui/material";
import { RenderByCargo } from "../../../../../utils/RenderByCargo";
import { Lead } from "../../../../../interfaces";
import * as Styles from "../styles";
import {
  ColumnConfig,
  isCorretor,
  isGerenteVendas,
  canViewEquipeColumn,
  isNonCorretor,
} from "../constants";
import {
  ActionsCell,
  LeadCell,
  EtapaCell,
  SubetapaCell,
  ObsCell,
  UltimaTarefaCell,
  PhoneCell,
  EmailCell,
  CorretorCell,
  UltimoResponsavelCell,
  EquipeCell,
  ProdutoCell,
  EtiquetaCell,
  DateCell,
  ExpiraEmCell,
  TemperaturaCell,
} from "./cells";

interface Props {
  leads: Lead[];
  visibleColumns: ColumnConfig[];
  codigoCargo: string | null;
  usuarioGerenteOnline?: boolean;
  isShared: boolean;
  checkedState: boolean[];
  onCheck: (
    index: number,
    lead: { idLead: string; idLeadCorretor: string; idCorretorLead?: string }
  ) => void;
  onUpdateClick: (leadId: string) => void;
  onMenuClick: (
    event: React.MouseEvent,
    leadId: string,
    leadCorretorId: string,
    leadNome: string
  ) => void;
  onEtiquetaClick: (event: React.MouseEvent, idLeadCorretor: string) => void;
  onEtapaClick: (item: Lead) => void;
  onProdutoClick: (item: Lead) => void;
  onObsClick: (item: Lead) => void;
  onWhatsAppClick: (item: Lead) => void;
  onWhatsAppCopyPhone: (item: Lead) => void;
  isWhatsConnected?: boolean;
  getWhatsAppUnreadCount: (item: Lead) => number;
}

const RowCell: React.FC<{
  column: ColumnConfig;
  item: Lead;
  index: number;
  props: Props;
}> = ({ column, item, index, props }) => {
  switch (column.id) {
    case "select":
      return (
        <Styles.FirstFixedTd key={column.id} style={{ marginLeft: 8 }}>
          <Checkbox
            size="small"
            name={item.idLead}
            value={item}
            checked={props.checkedState[index]}
            onChange={() =>
              props.onCheck(index, {
                idLead: item.idLead,
                idLeadCorretor: item.idLeadCorretor,
                idCorretorLead: item.idCorretorLead,
              })
            }
          />
        </Styles.FirstFixedTd>
      );

    case "actions":
      // Gerente online é somente leitura: não renderiza ações (Atualizar / menu).
      if (props.usuarioGerenteOnline) return null;
      return (
        <RenderByCargo
          key={column.id}
          cargo={props.codigoCargo}
          isCorretor={
            <ActionsCell
              leadId={item.idLead}
              leadCorretorId={item.idLeadCorretor}
              leadNome={item.nome}
              onUpdateClick={props.onUpdateClick}
              onMenuClick={props.onMenuClick}
            />
          }
        />
      );

    case "lead":
      return (
        <LeadCell
          key={column.id}
          item={item}
          codigoCargo={props.codigoCargo}
          usuarioGerenteOnline={props.usuarioGerenteOnline}
        />
      );

    case "subetapa":
      return (
        <SubetapaCell key={column.id} subEtapaFunil={item.subEtapaFunil} />
      );

    case "obs":
      return (
        <ObsCell
          key={column.id}
          observacoes={item?.observacoes}
          onClick={
            props.usuarioGerenteOnline
              ? undefined
              : () => props.onObsClick(item)
          }
        />
      );

    case "ultimaTarefa":
      return isCorretor(props.codigoCargo) ? (
        <UltimaTarefaCell
          key={column.id}
          observacaoUltimaTarefa={item.observacaoUltimaTarefa}
        />
      ) : null;

    case "telefone":
      return (
        <PhoneCell
          key={column.id}
          item={item}
          onWhatsAppClick={props.onWhatsAppClick}
          onWhatsAppCopyPhone={props.onWhatsAppCopyPhone}
          isWhatsConnected={props.isWhatsConnected}
          unreadCount={props.getWhatsAppUnreadCount(item)}
        />
      );

    case "email":
      return isCorretor(props.codigoCargo) ? (
        <EmailCell key={column.id} item={item} />
      ) : null;

    case "corretorResponsavel":
      return isNonCorretor(props.codigoCargo) ? (
        <CorretorCell key={column.id} item={item} />
      ) : null;

    case "ultimoResponsavel":
      return isGerenteVendas(props.codigoCargo) ? (
        <UltimoResponsavelCell key={column.id} item={item} />
      ) : null;

    case "equipe":
      return canViewEquipeColumn(
        props.codigoCargo,
        props.usuarioGerenteOnline
      ) ? (
        <EquipeCell key={column.id} item={item} />
      ) : null;

    case "produto":
      return (
        <ProdutoCell
          key={column.id}
          item={item}
          onProdutoClick={props.onProdutoClick}
          readOnly={props.usuarioGerenteOnline}
        />
      );

    case "etiqueta":
      return isCorretor(props.codigoCargo) ||
        props.codigoCargo === "219.840.012" ? (
        <EtiquetaCell
          key={column.id}
          item={item}
          isShared={props.isShared}
          onEtiquetaClick={props.onEtiquetaClick}
        />
      ) : null;

    case "dataCriacao":
      return <DateCell key={column.id} date={item.dataCriacao} />;

    case "expiraEm":
      return (
        <ExpiraEmCell
          key={column.id}
          farolAtualizacao={item.farolAtualizacao}
          mensagemFarolAtualizacao={item.mensagemFarolAtualizacao}
        />
      );

    case "dataAtualizacao":
      return <DateCell key={column.id} date={item.dataUltimaAtualizacao} />;

    case "etapa":
      return (
        <EtapaCell
          key={column.id}
          item={item}
          codigoCargo={props.codigoCargo}
          onEtapaClick={props.onEtapaClick}
        />
      );

    case "temperatura":
      return (
        <TemperaturaCell
          key={column.id}
          temperaturaLead={item.temperaturaLead}
        />
      );

    default:
      return null;
  }
};

export const TableBody: React.FC<Props> = (props) => {
  const { leads, visibleColumns } = props;

  return (
    <>
      {leads.map((item, index) => (
        <Styles.THead
          key={item.idLead}
          style={{
            backgroundColor:
              index % 2 === 0
                ? item.check
                  ? "#D5DEFF"
                  : "#FBFCFF"
                : item.check
                ? "#D5DEFF"
                : "white",
          }}
        >
          <Styles.Tr>
            {visibleColumns.map((column) => (
              <RowCell
                key={column.id}
                column={column}
                item={item}
                index={index}
                props={props}
              />
            ))}
          </Styles.Tr>
        </Styles.THead>
      ))}
    </>
  );
};
